import { comboKey } from "@/domain/profile-resolver/index";
import type { Category, CategoryGroup, Layer, Layers } from "@/domain/types";
import type { SC2Table } from "./index";

export const SC2_CATEGORIES: Record<string, Category> = {
  control: { label: "Control Groups", hue: 255 },
  // Unit Selection reads near Control Groups (both bluish) but is pulled well
  // off control's 255 toward cyan so the two no longer blur (the design-cleanup ask: push
  // the hue further from CGs). Sits clear of Cameras (150) on the other side.
  selection: { label: "Unit Selection", hue: 210 },
  camera: { label: "Cameras", hue: 150 },
  command: { label: "Commands", hue: 28 },
  ability: { label: "Abilities", hue: 28 },
  structure: { label: "Build", hue: 28 },
  unit: { label: "Train / Morph", hue: 28 },
  other: { label: "Other", hue: null },
};

// The design prototype's grouping, plus Other: command, ability, structure, and unit
// share hue 28 by design, so the list groups by id and folds them into one section.
// Selection sits with Control Groups (both blue); Other catches the uncurated
// [Hotkeys] fallthrough so no Binding vanishes from the list.
export const SC2_CATEGORY_GROUPS: CategoryGroup[] = [
  { id: "commands", label: "Commands", members: ["command", "ability", "structure", "unit"] },
  { id: "control", label: "Control Groups", members: ["control"] },
  { id: "selection", label: "Unit Selection", members: ["selection"] },
  { id: "camera", label: "Cameras", members: ["camera"] },
  { id: "other", label: "Other", members: ["other"] },
];

interface HotkeyRule {
  re: RegExp;
  /** Rules without make are omissions: menu, social, and UI ids are curated out. */
  make?: (m: RegExpMatchArray) => {
    name: string;
    short?: string;
    category: string;
    note?: string;
    cmd?: string;
  };
}

// Selection and Smart Command are the same in-game actions whether on the mouse (the game default)
// or on a key (a file can rebind either). One spec per action drives both the mouse default
// (sc2-ingest MOUSE_META) and the [Hotkeys] make-rule below, so moving the action onto a key keeps
// the curated note and icon on the mouse zone instead of the flatten clobbering it with a generic
// binding, and the key alternate carries the same binding. The note rides the command, not the
// physical button.
//
// Name is the in-game label (GameStrings UI/Hotkey/Name: "Selection", "Smart Commands"). The note
// is NOT from game data: SC2 hardcodes these mouse modifiers (Shift = add/queue, Ctrl = select
// type) with no hotkey id and no tooltip, so they cannot be derived. The real GameStrings tooltips
// describe unrelated behavior (click-vs-drag, target mode); see defaults/PROVENANCE.md.
export const SELECTION = {
  name: "Selection",
  category: "command",
  cmd: "_mouseSelect",
  note: "Shift: add, Ctrl: select type",
} as const;

export const SMART_COMMAND = {
  name: "Smart Commands",
  short: "Smart Cmd",
  category: "command",
  cmd: "_mouseSmart",
  note: "Shift: queue",
} as const;

// Curated view of the [Hotkeys] section (domain.md: gameplay shown, menu/social/UI
// omitted). First matching rule wins; unmatched ids fall through to a neutral binding.
// Names are the in-game labels (the bindings-list / readout); the terse forms (CG N, Cam N, ...)
// ride along as the cap `short` so the keycap legend stays compact.
const RULES: HotkeyRule[] = [
  { re: /^(Chat|Menu|Status|Dialog)/ },
  { re: /^(FPS|Music|Sound|PTT|GameTooltipsOn|QuickSave|MinimapColors|MinimapTerrain)$/ },
  // The observer / replay / spectator suite: only an observer or a replay viewer (never a player)
  // uses these, so they are not gameplay content. Older TheCore exports serialize the full SC2 hotkey
  // set, so they surface in editions 1-3; newer exports omit them. Curating them out drops them in
  // every edition (domain.md: gameplay shown, observer / replay / spectator omitted). Display-only:
  // the vendored download bytes are untouched.
  { re: /^(Observe|Replay|Leader|StatPanel)/ },
  { re: /^(NamePanel|TeamResources|ToggleVersusModeSides|ToggleWorldPanel)$/ },
  // Non-gameplay system binds: pause, skip-cinematic, and cancel-an-in-progress-drag-select are input
  // mechanics, not bindable command-card actions, so they are omitted too. Real in-game binds the file
  // carries - Smart Command (a player can move it off the mouse onto a key), camera push (Drag Scroll),
  // and jump-to-alert - are NOT omitted: they have curated make-rules below (camera / command).
  { re: /^(PauseGame|CinematicSkip|SelectionCancelDrag)$/ },
  // The five control-group operations, named by their in-game labels with the terse "CG N" form kept
  // as the cap short. The AndSteal regexes are mutually exclusive with the plain rules; order is only
  // for reading. Recall = select, Assign = create, Append = add; the steal variants also pull the
  // units out of any group they already belong to.
  {
    re: /^ControlGroupRecall(\d)$/,
    make: (m) => ({
      name: `Select Control Group ${m[1]}`,
      short: `CG ${m[1]}`,
      category: "control",
    }),
  },
  {
    re: /^ControlGroupAssign(\d)$/,
    make: (m) => ({
      name: `Create Control Group ${m[1]}`,
      short: `Create CG ${m[1]}`,
      category: "control",
    }),
  },
  {
    re: /^ControlGroupAssignAndSteal(\d)$/,
    make: (m) => ({
      name: `Create Control Group ${m[1]} (steal)`,
      short: `Create+Stl CG ${m[1]}`,
      category: "control",
    }),
  },
  {
    re: /^ControlGroupAppend(\d)$/,
    make: (m) => ({
      name: `Add to Control Group ${m[1]}`,
      short: `Add CG ${m[1]}`,
      category: "control",
    }),
  },
  {
    re: /^ControlGroupAppendAndSteal(\d)$/,
    make: (m) => ({
      name: `Add to Control Group ${m[1]} (steal)`,
      short: `Add+Stl CG ${m[1]}`,
      category: "control",
    }),
  },
  // Cam N / Save N keep the file's id-based numbering (the in-game menu labels them "Location N+1",
  // an off-by-one we deliberately do not adopt).
  {
    re: /^CameraView(\d)$/,
    make: (m) => ({ name: `Jump to Camera ${m[1]}`, short: `Cam ${m[1]}`, category: "camera" }),
  },
  {
    re: /^CameraSave(\d)$/,
    make: (m) => ({ name: `Save Camera ${m[1]}`, short: `Save ${m[1]}`, category: "camera" }),
  },
  {
    re: /^CameraCenter$/,
    make: () => ({ name: "Center on Current Selection", short: "Center", category: "camera" }),
  },
  {
    re: /^CameraFollow$/,
    make: () => ({ name: "Follow Current Selection", short: "Follow", category: "camera" }),
  },
  // The file ids say "Turn", but SC2's own Hotkeys menu labels these "Rotate".
  {
    re: /^CameraTurnLeft$/,
    make: () => ({ name: "Rotate Left", category: "camera", note: "Rotate the camera left" }),
  },
  {
    re: /^CameraTurnRight$/,
    make: () => ({ name: "Rotate Right", category: "camera", note: "Rotate the camera right" }),
  },
  // Real in-game camera binds a file can carry (kept, not omitted): the WASD-style drag-scroll mode
  // and the jump-to-last-alert camera recall. Names from the in-game labels.
  {
    re: /^CameraPush$/,
    make: () => ({ name: "Drag Scroll", category: "camera", note: "Camera follows the mouse" }),
  },
  {
    re: /^AlertRecall$/,
    make: () => ({
      name: "Jump to Last Alert",
      short: "Last Alert",
      category: "camera",
      note: "Press again to cycle alerts",
    }),
  },
  {
    re: /^TownCamera$/,
    make: () => ({ name: "Base Camera", category: "camera" }),
  },
  {
    re: /^ArmySelect$/,
    make: () => ({ name: "Select Army Units", short: "All Army", category: "selection" }),
  },
  {
    re: /^IdleWorker$/,
    make: () => ({ name: "Idle Worker", category: "selection" }),
  },
  { re: /^WarpIn$/, make: () => ({ name: "Warp In", category: "command" }) },
  {
    re: /^SubgroupNext$/,
    make: () => ({ name: "Next Subgroup", category: "command" }),
  },
  {
    re: /^SubgroupPrev$/,
    make: () => ({ name: "Previous Subgroup", short: "Prev Subgroup", category: "command" }),
  },
  // TargetChoose is diverted to the rapid-fire overlay:
  // its alternatives sit on the command caps and would shadow every one of them
  { re: /^TargetChoose$/ },
  {
    re: /^QuickPing$/,
    make: () => ({ name: "Quick Ping", short: "Ping", category: "command", note: "Alert allies" }),
  },
  {
    re: /^MinimapPing$/,
    make: () => ({
      name: "Ping Minimap",
      short: "Ping",
      category: "command",
      note: "Alert allies",
    }),
  },
  {
    re: /^CommanderAbility(\d)$/,
    make: (m) => ({
      name: `Commander Ability ${m[1]}`,
      short: `Cmdr ${m[1]}`,
      category: "command",
    }),
  },
  // Selection / Smart Command rebound onto a key: the same specs the mouse defaults use, so the
  // mouse zone keeps its curated binding and the keyboard alternate renders it too.
  { re: /^Selection$/, make: () => ({ ...SELECTION }) },
  { re: /^SmartCommand$/, make: () => ({ ...SMART_COMMAND }) },
];

/** Fallback name for an uncurated id: split the camel case ("SomeNewToggle" gives "Some New Toggle"). */
function prettify(id: string): string {
  return id.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
}

/**
 * The curated binding for a hotkey id, or null when a rule omits it (menu, social, UI).
 * First matching rule wins; an unmatched id falls through to a neutral binding. Matching
 * once per rule feeds the capture groups straight to make, with no separate test pass.
 */
export function bindingFor(
  id: string,
): { name: string; short?: string; category: string; note?: string; cmd?: string } | null {
  for (const rule of RULES) {
    const m = id.match(rule.re);
    if (m) return rule.make ? rule.make(m) : null;
  }
  return { name: prettify(id), category: "other" };
}

/**
 * The board view of a parsed file: the global [Hotkeys] section flattened into
 * positional modifier layers, exact-combo keyed. Every alternative places, each on the
 * layer its own combo names. The [Commands] table needs the contextual
 * command card and is not rendered here. First binding wins per key and combo:
 * TheCore files set AllowSetConflicts=1 and the earlier entry is the file's primary meaning.
 */
export function hotkeysLayers(table: SC2Table): Layers {
  const base: Layer = {};
  const mods: Record<string, Layer> = {};
  for (const [id, alternatives] of Object.entries(table.hotkeys)) {
    const binding = bindingFor(id);
    if (!binding) continue; // a rule curated this id out
    for (const hk of alternatives) {
      const combo = comboKey(hk.mods);
      const layer = combo ? (mods[combo] ?? {}) : base;
      if (combo) mods[combo] = layer;
      // every emitting key fires the hotkey in game, so every code carries the binding
      for (const code of hk.codes) {
        if (!(code in layer)) layer[code] = binding;
      }
    }
  }
  const layers: Layers = { base };
  if (Object.keys(mods).length > 0) layers.mods = mods;
  return layers;
}

/**
 * Distinct curated [Hotkeys] bindings in a table: one per id a rule curates in
 * (bindingFor non-null) that has at least one mappable alternative. The import toast
 * reports this, not boundCount's positional-cell total - hotkeysProfile fans one
 * binding over every emitting key and drops cells that collide on a code, so the
 * cell total over- and under-counts the distinct entries. Mirrors commandBindingCount.
 */
export function hotkeyBindingCount(table: SC2Table): number {
  return Object.entries(table.hotkeys).filter(
    ([id, alternatives]) => bindingFor(id) != null && alternatives.some((a) => a.codes.length > 0),
  ).length;
}
